/**
 * ServiceFeesSection — Admin: Globale Servicegebühren
 *
 * Konfiguriert 5 Fee-Slots (4× Prozent + 1× EUR-Flat).
 * Änderungen wirken sofort für alle Buchungen (User-Overrides überschreiben pro User).
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { AlertCircle, Save, Loader2, Percent, Euro, RotateCcw } from "lucide-react";
import CreatorSubscriptionBoundsPanel from "@/components/admin/CreatorSubscriptionBoundsPanel";

const AREA_LABELS = {
  STX_SHOP: { label: "stilltouchedX Shop", type: "pct", desc: "Prozent-Anteil der Plattform am Verkauf im STX-Shop." },
  STX_RESCUE: { label: "STX Rescue / Wundertüten", type: "pct", desc: "Erhöhter Anteil an Rescue-Kits & Wundertüten." },
  SUBSCRIPTION: { label: "Creator-Abo (Social)", type: "pct", desc: "Anteil der Plattform an monatlichen Creator-Abos." },
  PAID_MEDIA: { label: "Einzelkauf-Content (Social)", type: "pct", desc: "Anteil der Plattform an Paid-Media-Einzelkäufen." },
  MP_BUYER_PROTECTION: { label: "MP Käuferschutz-Fee", type: "flat", desc: "Flatfee (EUR) bei aktivem Käuferschutz-Optin im Marketplace-Checkout. Wird 1:1 der Plattform gutgeschrieben." },
};

const ORDERED_AREAS = ["STX_SHOP", "STX_RESCUE", "SUBSCRIPTION", "PAID_MEDIA", "MP_BUYER_PROTECTION"];

export default function ServiceFeesSection() {
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/service-fees");
      setConfig(data.config);
      setDefaults(data.defaults);
      // Seed inputs from config
      const seeded = {};
      ORDERED_AREAS.forEach((a) => {
        seeded[a] = displayValue(a, data.config[a] ?? data.defaults[a]);
      });
      setValues(seeded);
    } catch (e) {
      setError(e?.response?.data?.detail || "Konnte Konfiguration nicht laden.");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  function displayValue(area, raw) {
    if (raw === undefined || raw === null) return "";
    const info = AREA_LABELS[area];
    if (info.type === "pct") return (Number(raw) * 100).toFixed(2);
    return Number(raw).toFixed(2);
  }

  function parseValue(area, str) {
    const num = Number(str);
    if (Number.isNaN(num)) throw new Error(`${AREA_LABELS[area].label}: Ungültiger Wert`);
    const info = AREA_LABELS[area];
    if (info.type === "pct") {
      if (num < 0 || num > 100) throw new Error(`${info.label}: Prozent muss 0–100 sein`);
      return Math.round((num / 100) * 10000) / 10000;
    }
    if (num < 0 || num > 999) throw new Error(`${info.label}: Wert unrealistisch`);
    return Math.round(num * 100) / 100;
  }

  async function save() {
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const payload = {};
      for (const area of ORDERED_AREAS) {
        payload[area] = parseValue(area, values[area]);
      }
      const { data } = await api.put("/admin/service-fees", { values: payload });
      setConfig(data.config);
      setSuccess("Gespeichert. Änderungen greifen ab sofort.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function resetToDefaults() {
    const seeded = {};
    ORDERED_AREAS.forEach((a) => {
      seeded[a] = displayValue(a, defaults[a]);
    });
    setValues(seeded);
    setSuccess("");
  }

  if (loading) {
    return (
      <div data-testid="service-fees-loading" className="flex items-center gap-2 text-sm text-[#666]">
        <Loader2 size={16} className="animate-spin" /> Lade Servicegebühren…
      </div>
    );
  }

  return (
    <div data-testid="service-fees-section" className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-bold text-[#0A0A0A]">Servicegebühren</h2>
        <p className="text-xs text-[#666] mt-0.5">
          Globale Fee-Konfiguration pro Bereich. Diese Werte gelten für alle Userklassen, außer es besteht ein User-spezifischer Override (siehe Benutzerverwaltung).
        </p>
      </div>

      {error && (
        <div data-testid="service-fees-error" className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-xs text-red-700">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div data-testid="service-fees-success" className="p-3 bg-green-50 border border-green-200 text-xs text-green-700">
          {success}
        </div>
      )}

      <div className="space-y-3">
        {ORDERED_AREAS.map((area) => {
          const info = AREA_LABELS[area];
          const defaultV = defaults?.[area];
          const currentV = config?.[area];
          const isPct = info.type === "pct";
          return (
            <div key={area} data-testid={`fee-row-${area}`} className="p-4 border border-[#E5E5E5] bg-white">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className="text-sm font-semibold text-[#0A0A0A]">{info.label}</p>
                  <p className="text-[11px] text-[#666] mt-0.5">{info.desc}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[#999]">
                  {isPct ? "Prozent" : "EUR"}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div className="relative flex-1">
                  <input
                    data-testid={`fee-input-${area}`}
                    type="number"
                    step={isPct ? "0.01" : "0.01"}
                    min="0"
                    max={isPct ? "100" : "999"}
                    value={values[area] ?? ""}
                    onChange={(e) => setValues({ ...values, [area]: e.target.value })}
                    className="w-full h-11 px-4 pr-9 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999]">
                    {isPct ? <Percent size={14} /> : <Euro size={14} />}
                  </span>
                </div>
                <span data-testid={`fee-default-${area}`} className="text-[10px] text-[#999] whitespace-nowrap w-24 text-right">
                  Default: {displayValue(area, defaultV)}{isPct ? "%" : " €"}
                </span>
              </div>
              {currentV !== undefined && currentV !== defaultV && (
                <p data-testid={`fee-diff-${area}`} className="mt-1 text-[10px] text-amber-700">
                  Aktueller Global-Wert weicht vom Default ab: {displayValue(area, currentV)}{isPct ? "%" : " €"}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 pt-2">
        <button
          data-testid="service-fees-save"
          onClick={save}
          disabled={saving}
          className="flex-1 h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#222] transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Speichert…" : "Speichern"}
        </button>
        <button
          data-testid="service-fees-reset"
          onClick={resetToDefaults}
          disabled={saving}
          className="h-11 px-4 border border-[#E5E5E5] text-[#666] text-xs font-medium flex items-center gap-1.5 hover:bg-[#F5F5F5] disabled:opacity-40"
          title="Auf Default-Werte zurücksetzen (noch nicht gespeichert)"
        >
          <RotateCcw size={12} /> Defaults
        </button>
      </div>

      {config?.updated_at && (
        <p data-testid="service-fees-updated-at" className="text-[10px] text-[#999]">
          Zuletzt geändert: {new Date(config.updated_at).toLocaleString("de-DE")}
          {config.updated_by ? ` von ${config.updated_by}` : ""}
        </p>
      )}

      {/* iter122: Creator-Abo-Preisgrenzen als eigene Unter-Sektion. */}
      <div className="pt-4 border-t border-[#E5E5E5]">
        <CreatorSubscriptionBoundsPanel />
      </div>
    </div>
  );
}
